"use client";

import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/ui/AppIcon';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}

export default function Modal({ isOpen, onClose, children, title }: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Use portal to render modal at document body level, outside of header hierarchy
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 overflow-y-auto">
      <div
        className="absolute inset-0 bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl bg-gradient-to-b from-[#1a2420] to-[#0f1512] rounded-3xl shadow-2xl border border-[#3a4d46]/30 animate-in zoom-in-95 duration-300 max-h-[85vh] flex flex-col my-auto">
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 bg-gradient-to-b from-[#1a2420] to-[#1a2420]/95 backdrop-blur-xl border-b border-[#3a4d46]/40">
          {title && <h2 className="text-2xl font-bold text-white tracking-tight">{title}</h2>}
          <button
            onClick={onClose}
            className="ml-auto p-2 text-[#a8c4b8] hover:text-white hover:bg-[#3a4d46]/50 rounded-xl transition-all duration-200 hover:rotate-90"
            aria-label="Close modal"
          >
            <Icon name="XMarkIcon" size={24} />
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">{children}</div>
      </div>
    </div>,
    document.body
  );
}